import { useState } from 'react';
import { api } from '../services/api';
import '../pages/LoginPage.css';

interface ResetPasswordModalProps {
  isOpen: boolean;
  username: string;
  onClose: () => void;
}

export const ResetPasswordModal = ({ isOpen, username, onClose }: ResetPasswordModalProps) => {
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [resetStatus, setResetStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [resetError, setResetError] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleClose = () => {
    setResetCode('');
    setNewPassword('');
    setResetStatus('idle');
    setResetError(null);
    onClose();
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetStatus('loading');
    setResetError(null);

    try {
      await api.resetPassword(username, resetCode, newPassword);
      setResetStatus('success');
    } catch (err) {
      setResetError(err instanceof Error ? err.message : 'Request failed. Please try again.');
      setResetStatus('error');
    }
  };

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Reset Password</h2>
          <button className="modal-close" onClick={handleClose}>&times;</button>
        </div>

        {resetStatus === 'success' ? (
          <div className="reset-success">
            <p>Your password has been reset. You can now sign in with your new password.</p>
            <button className="btn btn-primary" onClick={handleClose}>Close</button>
          </div>
        ) : (
          <form onSubmit={handleResetPassword}>
            <p className="modal-description">Enter the verification code sent to your email and choose a new password.</p>

            {resetStatus === 'error' && (
              <div className="alert alert-error">{resetError}</div>
            )}

            <div className="form-group">
              <label htmlFor="reset-code">Verification Code</label>
              <input
                type="text"
                id="reset-code"
                className="form-input"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                required
                placeholder="Enter the code from your email"
                disabled={resetStatus === 'loading'}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="new-password">New Password</label>
              <input
                type="password"
                id="new-password"
                className="form-input"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                autoComplete="new-password"
                placeholder="Enter your new password"
                disabled={resetStatus === 'loading'}
              />
            </div>

            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={handleClose} disabled={resetStatus === 'loading'}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary" disabled={resetStatus === 'loading'}>
                {resetStatus === 'loading' ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};
